import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Leave() {
  const { user } = useAuth();
  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "CASUAL", from_date: "", to_date: "", reason: "" });

  const load = async () => { const { data } = await api.get("/sales/leaves"); setItems(data); };
  useEffect(() => { load(); }, []);
  const apply = async () => { try { await api.post("/sales/leaves", form); toast.success("Applied"); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const decide = async (l, action) => { try { await api.post(`/sales/leaves/${l.id}/decide`, { action }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-leave">
      <div className="flex items-start justify-between">
        <div><h1 className="font-display text-2xl sm:text-3xl font-black">Leave</h1><p className="text-muted-foreground">Apply for leave; manager approves.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="leave-apply-btn"><Plus className="w-4 h-4 mr-1" /> Apply</Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>By</TableHead><TableHead>Kind</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                <TableCell className="font-mono text-xs">{l.from_date}</TableCell>
                <TableCell className="font-mono text-xs">{l.to_date}</TableCell>
                <TableCell className="text-sm">{l.user_email}</TableCell>
                <TableCell><Badge variant="outline">{l.kind}</Badge></TableCell>
                <TableCell className="text-xs">{l.reason || "—"}</TableCell>
                <TableCell><Badge>{l.status}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  {isManager && l.status === "PENDING" && (
                    <>
                      <Button size="sm" onClick={() => decide(l, "APPROVE")} data-testid={`leave-approve-${l.id}`}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => decide(l, "REJECT")} data-testid={`leave-reject-${l.id}`}><X className="w-4 h-4" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No leave applications.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="leave-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{["CASUAL","SICK","PRIVILEGED","UNPAID"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="leave-from" /></div>
            <div><Label>To</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="leave-to" /></div>
            <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={apply} data-testid="leave-save">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
